import { useState } from "react";
import { Plus } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import {
  Page,
  Table,
  type Column,
  Badge,
  Button,
  Switch,
  Checkbox,
  Combobox,
  FormModal,
  FormSectionLabel,
  Input,
  RelativeDateField,
  Text,
  EmptyState,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { useIsAdmin } from "../../auth/useAuth";
import {
  useAPIKeyList,
  useCreateAPIKey,
  useDeleteAPIKey,
  useRevokeAPIKey,
  type APIKey,
} from "../../lib/queries/apiKeys";
import { useSenderList, type Sender } from "../../lib/queries/senders";
import { useTemplateList, type Template } from "../../lib/queries/templates";
import { TokenRevealModal } from "../../components/TokenRevealModal";
import { RefreshButton } from "../../components/RefreshButton";
import { labelCategory } from "../MessagesPage/messageConstants";

interface APIKeyFormValues {
  name: string;
  sender_keys: string[];
  template_keys: string[];
  transactional: boolean;
  marketing: boolean;
}

function CreateAPIKeyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (token: string) => void;
}) {
  const create = useCreateAPIKey();
  const senders = useSenderList();
  const templates = useTemplateList();
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<APIKeyFormValues>({
    mode: "onTouched",
    defaultValues: {
      name: "",
      sender_keys: [],
      template_keys: [],
      transactional: true,
      marketing: false,
    },
  });

  async function onSubmit(values: APIKeyFormValues) {
    const allowed_categories = [
      ...(values.transactional ? ["transactional" as const] : []),
      ...(values.marketing ? ["marketing" as const] : []),
    ];
    try {
      const created = await create.mutateAsync({
        name: values.name.trim(),
        sender_keys: values.sender_keys,
        template_keys: values.template_keys,
        allowed_categories,
      });
      notifySuccess("The API key was created.");
      onCreated(created.token);
    } catch (err) {
      apiErrorToFormErrors<APIKeyFormValues>(err, setError, {
        fallbackMessage: "Creation failed. Please try again.",
      });
    }
  }

  return (
    <FormModal
      open
      title="New API key"
      onClose={onClose}
      width={520}
      busy={create.isPending}
      error={rootErrorMessage(errors)}
      submitLabel="Create"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        label="Name"
        required
        placeholder="e.g. shop-production"
        error={errors.name?.message}
        {...register("name", { required: "Required field" })}
      />

      <FormSectionLabel>Permissions</FormSectionLabel>
      <Controller
        name="sender_keys"
        control={control}
        render={({ field }) => (
          <Combobox<Sender, string>
            label="Allowed senders"
            hint="Empty = all senders."
            options={senders.data ?? []}
            getOptionLabel={(s) => `${s.name} (${s.key})`}
            getOptionValue={(s) => s.key}
            getOptionKey={(s) => s.id}
            multiple
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <Controller
        name="template_keys"
        control={control}
        render={({ field }) => (
          <Combobox<Template, string>
            label="Allowed templates"
            hint="Empty = all templates."
            options={templates.data ?? []}
            getOptionLabel={(t) => `${t.name} (${t.key})`}
            getOptionValue={(t) => t.key}
            getOptionKey={(t) => t.id}
            multiple
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <Checkbox label="Transactional messages" {...register("transactional")} />
      <Checkbox label="Marketing messages" {...register("marketing")} />
    </FormModal>
  );
}

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "reveal"; token: string };

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["api-keys"], ["senders"], ["templates"]];

export function ApiKeysPage() {
  const isAdmin = useIsAdmin();
  // A key an admin has tidied away. Revoked ones stay in the list — that is
  // recent news — and only what was explicitly deleted hides.
  const [showDeleted, setShowDeleted] = useState(false);
  const { data, isLoading, error, refetch } = useAPIKeyList(showDeleted);
  const deletedCount = (data ?? []).filter((k) => k.deleted).length;
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const confirm = useConfirm();
  const revoke = useRevokeAPIKey();
  const remove = useDeleteAPIKey();

  function confirmDelete(apiKey: APIKey) {
    void confirm({
      title: "Delete API key",
      // Said plainly, because "delete" is not what happens: the row is kept —
      // the messages it sent record it as their sender — and only hidden from
      // this list.
      description: (
        <>
          Hide <strong>{apiKey.name}</strong> from this list? The key itself is kept —
          the messages it sent still name it as their sender — and it is already
          revoked, so it can never be used again. Turn on “Show deleted” to find it.
        </>
      ),
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: async () => {
        await remove.mutateAsync({ id: apiKey.id });
      },
    }).then((confirmed) => {
      if (confirmed) notifySuccess("The API key was hidden.");
    });
  }

  function confirmRevoke(apiKey: APIKey) {
    void confirm({
      title: "Revoke API key",
      description: (
        <>
          Really revoke the key <strong>{apiKey.name}</strong>? Applications
          using it will lose access immediately and irreversibly.
        </>
      ),
      confirmLabel: "Revoke",
      variant: "danger",
      onConfirm: async () => {
        await revoke.mutateAsync({ id: apiKey.id });
      },
    }).then((confirmed) => {
      if (confirmed) notifySuccess("The API key was revoked.");
    });
  }

  const columns: Column<APIKey>[] = [
    {
      key: "name",
      header: "Name",
      primary: true,
      render: (row) => row.name,
    },
    {
      key: "senders",
      header: "Senders",
      render: (row) =>
        row.sender_keys.length ? (
          <Text variant="mono">{row.sender_keys.join(", ")}</Text>
        ) : (
          <Text variant="muted">all</Text>
        ),
    },
    {
      key: "templates",
      header: "Templates",
      render: (row) =>
        row.template_keys.length ? (
          <Text variant="mono">{row.template_keys.join(", ")}</Text>
        ) : (
          <Text variant="muted">all</Text>
        ),
    },
    {
      key: "categories",
      header: "Categories",
      render: (row) =>
        row.allowed_categories.length ? (
          <>
            {row.allowed_categories.map((c) => (
              <Badge key={c} variant="info">
                {labelCategory(c)}
              </Badge>
            ))}
          </>
        ) : (
          <Text variant="muted">all</Text>
        ),
    },
    {
      key: "last_used",
      header: "Last used",
      render: (row) =>
        row.last_used_at ? (
          <RelativeDateField record={row} source="last_used_at" />
        ) : (
          <Text variant="muted">never</Text>
        ),
    },
    {
      key: "active",
      header: "Status",
      render: (row) =>
        row.active ? (
          <Badge weight="ghost" variant="ok" dot>Active</Badge>
        ) : row.deleted ? (
          <Badge weight="ghost" variant="muted" dot>Deleted</Badge>
        ) : (
          <Badge weight="ghost" variant="muted" dot>Revoked</Badge>
        ),
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            header: "",
            render: (row: APIKey) => (
              <Table.Actions>
                {row.active ? (
                  <Table.Action variant="danger" onClick={() => confirmRevoke(row)}>
                    Revoke
                  </Table.Action>
                ) : row.deleted ? null : (
                  <Table.Action variant="danger" onClick={() => confirmDelete(row)}>
                    Delete
                  </Table.Action>
                )}
              </Table.Actions>
            ),
          } satisfies Column<APIKey>,
        ]
      : []),
  ];

  return (
    <>
      <Page>
        <Page.Header
          title="API keys"
          subtitle="application access to the sending API"
          subtitleVariant="tagline"
          actions={
            <>
              <Switch
                label={showDeleted ? `Deleted shown (${deletedCount})` : "Show deleted"}
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
              />
              <RefreshButton queryKeys={REFRESH_KEYS} />
              {isAdmin && (
                <Button variant="primary" onClick={() => setModal({ kind: "create" })}>
                  <Plus size={16} aria-hidden="true" />
                  Create key
                </Button>
              )}
            </>
          }
        />
        <Page.Content stack gap="lg">
          <Table<APIKey>
            striped
            cards
            columns={columns}
            rows={data ?? []}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="No API keys"
                description="Create a key so applications can connect."
              />
            }
          />
        </Page.Content>
      </Page>

      {modal.kind === "create" && (
        <CreateAPIKeyModal
          onClose={() => setModal({ kind: "closed" })}
          onCreated={(token) => setModal({ kind: "reveal", token })}
        />
      )}

      <TokenRevealModal
        open={modal.kind === "reveal"}
        title="API key created"
        noun="API key"
        token={modal.kind === "reveal" ? modal.token : ""}
        onClose={() => setModal({ kind: "closed" })}
      />
    </>
  );
}
